import { Box, HStack, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { keyframes } from '@chakra-ui/system';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import CodeFrame from 'components/DevSandbox/CodeFrame';
import DevSandbox from 'components/DevSandbox/DevSandbox';
import { useDevSandbox } from 'components/DevSandbox/DevSandboxProvider';
import EditorSettingsButton, { ShareButton } from 'components/DevSandbox/EditorSettingsButton';
import { VibeCodeButton } from 'components/DevSandbox/Merlin';
import { TEditorFramework, TCSSMode } from 'components/DevSandbox/types';
import GitHubButton from 'components/GitHubButton';
import { RedirectModal } from 'components/Modal/RedirectModal';
import { SEO } from 'components/SEO';
import { useMediaQueries } from 'layout/MediaQueriesProvider';

import {
  decodeParam,
  detectFrameworkFromCode,
  encodeParam,
  getCssParamName,
  getJsParamName,
  resolveCssParam,
  resolveJsParam,
} from './demoUtils';
import { DEMO_SPELLS, getRandomSpellIndex } from './merlinDemoSpells';
import { DEMO_TEMPLATES, DemoTemplate } from './templates';

const VISIBLE_TEMPLATES = DEMO_TEMPLATES.filter((t) => !t.hidden);

const CUSTOM_DEFAULT = {
  html: `<div class="custom-welcome">
  <div class="icon">&#x1F3A8;</div>
  <h1>Your Playground</h1>
  <p class="tagline">Write HTML, CSS & JS here &mdash; the URL updates in real-time.</p>

  <div class="features">
    <div class="feature">
      <span class="emoji">&#x1F517;</span>
      <strong>Shareable URL</strong>
      <span class="desc">Your code is encoded in the URL. Copy &amp; share it with anyone.</span>
    </div>
    <div class="feature">
      <span class="emoji">&#x26A1;</span>
      <strong>Live Preview</strong>
      <span class="desc">Every edit updates the preview instantly, no save needed.</span>
    </div>
    <div class="feature">
      <span class="emoji">&#x1FA84;</span>
      <strong>Try Merlin AI</strong>
      <span class="desc">Click <em>Vibe Code</em> above and type anything for a surprise!</span>
    </div>
  </div>

  <p class="start">Edit any tab to get started. This content will be replaced by your code.</p>
</div>`,
  css: `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: #1a1a2e;
  color: #e2e8f0;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 2rem;
}

.custom-welcome {
  text-align: center;
  max-width: 480px;
}

.icon {
  font-size: 3rem;
  margin-bottom: 0.75rem;
}

h1 {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #7c3aed, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.tagline {
  color: #94a3b8;
  margin-bottom: 2rem;
  font-size: 1rem;
}

.features {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
  text-align: left;
}

.feature {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;
  background: rgba(255, 255, 255, 0.04);
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.feature .emoji {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.feature strong {
  color: #e2e8f0;
  margin-right: 0.25rem;
}

.feature .desc {
  color: #94a3b8;
  font-size: 0.875rem;
}

.feature em {
  color: #a78bfa;
  font-style: normal;
  font-weight: 600;
}

.start {
  color: #64748b;
  font-size: 0.8rem;
}`,
  js: `// Your JavaScript goes here
// Every change auto-updates the shareable URL`,
};

// Inline chevron SVG — 10px, matches IDE dropdown aesthetic
const ChevronDown = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ opacity: 0.5 }}>
    <path d="M2.5 3.5L5 6.5L7.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TemplateSelector = ({
  templates,
  selected,
  onSelect,
}: {
  templates: DemoTemplate[];
  selected: string;
  onSelect: (id: string) => void;
}) => {
  const selectedTemplate = templates.find((t) => t.id === selected);
  const selectedIcon = selected === 'custom' ? '✨' : (selectedTemplate?.icon || '');
  const selectedLabel = selected === 'custom' ? 'Custom' : (selectedTemplate?.title || 'Templates');

  // All items including Custom at the end
  const allItems = [
    ...templates.map((t) => ({ id: t.id, icon: t.icon, title: t.title })),
    { id: 'custom', icon: '✨', title: 'Custom' },
  ];

  return (
    <HStack spacing={0} alignItems="center">
      <Box w="1px" h="20px" bg="whiteAlpha.200" mx={3} flexShrink={0} />
      <Menu placement="bottom-start" gutter={4} strategy="fixed">
        <MenuButton
          as={Box}
          px={3}
          py="5px"
          borderRadius="md"
          cursor="pointer"
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.200"
          color="whiteAlpha.800"
          transition="all 0.15s"
          _hover={{ bg: 'whiteAlpha.100', borderColor: 'whiteAlpha.300', color: 'white' }}
          fontSize="12px"
          fontWeight="500"
        >
          <HStack spacing="8px" whiteSpace="nowrap">
            <Box as="span" fontSize="13px" lineHeight={1}>{selectedIcon}</Box>
            <Box as="span">{selectedLabel}</Box>
            <ChevronDown />
          </HStack>
        </MenuButton>
      <MenuList
        bg="gray.900"
        borderColor="whiteAlpha.100"
        p="4px"
        zIndex="popover"
        minW="220px"
        boxShadow="0 4px 12px rgba(0,0,0,0.5)"
        borderRadius="lg"
      >
        {allItems.map((t) => (
          <Box key={t.id}>
            {/* Divider before Custom */}
            {t.id === 'custom' && (
              <Box height="1px" bg="whiteAlpha.50" my={1} />
            )}
            <MenuItem
              onClick={() => onSelect(t.id)}
              bg={selected === t.id ? 'whiteAlpha.100' : 'transparent'}
              _hover={{ bg: selected === t.id ? 'whiteAlpha.150' : 'whiteAlpha.50' }}
              _active={{ bg: 'whiteAlpha.100' }}
              _focus={{ bg: selected === t.id ? 'whiteAlpha.100' : 'whiteAlpha.50' }}
              fontSize="12px"
              fontWeight={selected === t.id ? '500' : '400'}
              color={selected === t.id ? 'white' : 'whiteAlpha.700'}
              h="32px"
              px={2}
              borderRadius="sm"
              whiteSpace="nowrap"
              transition="background 0.1s"
            >
              <HStack spacing={2}>
                <Box as="span" fontSize="13px" lineHeight={1} w="20px" textAlign="center">{t.icon}</Box>
                <Box as="span">{t.title}</Box>
              </HStack>
            </MenuItem>
          </Box>
        ))}
      </MenuList>
      </Menu>
    </HStack>
  );
};

const bgDrift = keyframes`
  0% { background-position: 0% 0%; }
  50% { background-position: 100% 100%; }
  100% { background-position: 0% 0%; }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.25; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.5; transform: translate(-50%, -50%) scale(1.2); }
`;

const orbFloat = keyframes`
  0% { transform: translateY(0); opacity: 0; }
  15% { opacity: 0.8; }
  85% { opacity: 0.8; }
  100% { transform: translateY(-160px); opacity: 0; }
`;

const shimmerText = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const wandFloat = keyframes`
  0%, 100% { transform: translateY(0) rotate(-5deg); }
  50% { transform: translateY(-6px) rotate(5deg); }
`;

const SPELL_MESSAGES = [
  'Brewing a spell...',
  'Weaving incantations...',
  'Channeling arcane energy...',
  'Summoning pixel spirits...',
  'Conjuring solutions...',
];

const FADE_OUT_MS = 600;

const SpellLoadingOverlay = ({ isFadingOut }: { isFadingOut: boolean }) => {
  const [msgIdx, setMsgIdx] = useState(() => Math.floor(Math.random() * SPELL_MESSAGES.length));

  useEffect(() => {
    const interval = setInterval(() => {
      setMsgIdx((prev) => (prev + 1) % SPELL_MESSAGES.length);
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex={10}
      overflow="hidden"
      bg="radial-gradient(ellipse at 30% 20%, rgba(88,28,135,0.15) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(124,58,237,0.1) 0%, transparent 50%), #0f0a1a"
      bgSize="200% 200%"
      animation={`${bgDrift} 12s ease-in-out infinite`}
      opacity={isFadingOut ? 0 : 1}
      transition={`opacity ${FADE_OUT_MS}ms ease-out`}
      pointerEvents={isFadingOut ? 'none' : 'auto'}
    >
      {/* Large ambient glow — center */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        w="400px"
        h="400px"
        borderRadius="full"
        bg="radial-gradient(circle, rgba(139,92,246,0.12) 0%, rgba(88,28,135,0.05) 50%, transparent 70%)"
        animation={`${glowPulse} 4s ease-in-out infinite`}
        pointerEvents="none"
      />

      {/* Secondary glow — offset */}
      <Box
        position="absolute"
        top="35%"
        left="60%"
        w="250px"
        h="250px"
        borderRadius="full"
        bg="radial-gradient(circle, rgba(167,139,250,0.08) 0%, transparent 70%)"
        animation={`${glowPulse} 5s ease-in-out 1s infinite`}
        pointerEvents="none"
      />

      {/* Floating orbs */}
      {[0, 1, 2, 3, 4, 5, 6].map((i) => {
        const sizes = [3, 4, 2, 5, 3, 2, 4];
        const lefts = [12, 28, 45, 58, 72, 85, 35];
        const bottoms = [35, 42, 38, 45, 40, 36, 50];
        const durations = [4, 3.5, 5, 3.2, 4.5, 3.8, 4.2];
        const delays = [0, 0.6, 1.2, 0.3, 0.9, 1.5, 0.4];
        const colors = ['purple.400', 'purple.300', 'purple.500', 'purple.300', 'purple.400', 'purple.500', 'purple.300'];

        return (
          <Box
            key={i}
            position="absolute"
            bottom={`${bottoms[i]}%`}
            left={`${lefts[i]}%`}
            w={`${sizes[i]}px`}
            h={`${sizes[i]}px`}
            borderRadius="full"
            bg={colors[i]}
            boxShadow={`0 0 ${sizes[i] * 3}px rgba(139,92,246,0.4)`}
            animation={`${orbFloat} ${durations[i]}s ease-in-out ${delays[i]}s infinite`}
            pointerEvents="none"
          />
        );
      })}

      {/* Content */}
      <Box textAlign="center" position="relative" zIndex={1}>
        <Box
          fontSize="2.5rem"
          mb={4}
          animation={`${wandFloat} 2.5s ease-in-out infinite`}
          sx={{ filter: 'drop-shadow(0 0 12px rgba(139,92,246,0.5))' }}
        >
          🪄
        </Box>

        <Text
          key={msgIdx}
          fontSize="sm"
          fontWeight="semibold"
          bgGradient="linear(to-r, purple.300, purple.200, purple.400, purple.300)"
          bgSize="200% auto"
          bgClip="text"
          animation={`${shimmerText} 3s linear infinite`}
          letterSpacing="0.03em"
        >
          {SPELL_MESSAGES[msgIdx]}
        </Text>
      </Box>
    </Box>
  );
};

const DemoPreview = () => {
  const { iframe, demoSpell } = useDevSandbox();
  const isLoading = demoSpell.state.isSpellLoading;

  // Overlay lifecycle: loading → waitForIframe → fadingOut → hidden
  //
  // The template Blob URL is NEVER generated when auto-spell is active
  // (DevSandboxProvider suppresses iframe updates until spell code is applied).
  // This means the first-ever iframe URL IS the spell code — no template content
  // can leak through the overlay. We just wait for CodeFrame's onReady signal
  // (which fires after the iframe loads the spell content).
  const [showOverlay, setShowOverlay] = useState(isLoading);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [waitingForIframe, setWaitingForIframe] = useState(false);

  // When loading starts, show overlay. When loading ends, wait for iframe onReady.
  useEffect(() => {
    if (isLoading) {
      setShowOverlay(true);
      setIsFadingOut(false);
      setWaitingForIframe(false);
    } else if (showOverlay && !waitingForIframe && !isFadingOut) {
      setWaitingForIframe(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Safety: if iframe never fires onReady within 3s, fade out anyway
  useEffect(() => {
    if (!waitingForIframe) return;
    const safety = setTimeout(() => {
      setWaitingForIframe(false);
      setIsFadingOut(true);
    }, 3000);
    return () => clearTimeout(safety);
  }, [waitingForIframe]);

  // Unmount overlay after fade-out transition completes
  useEffect(() => {
    if (!isFadingOut) return;
    const timer = setTimeout(() => {
      setShowOverlay(false);
      setIsFadingOut(false);
    }, FADE_OUT_MS);
    return () => clearTimeout(timer);
  }, [isFadingOut]);

  // Ref so the callback always sees the latest value without re-creating.
  const waitingRef = useRef(waitingForIframe);
  waitingRef.current = waitingForIframe;

  // Called by CodeFrame when iframe content finishes loading.
  // Since the template URL is suppressed, the first onReady is always for spell code.
  const handleIframeReady = useCallback(() => {
    if (waitingRef.current) {
      setWaitingForIframe(false);
      setIsFadingOut(true);
    }
  }, []);

  return (
    <Box width="100%" height="100%" position="relative">
      <CodeFrame url={iframe.codeFrameUrl || ''} onReady={handleIframeReady} />
      {showOverlay && <SpellLoadingOverlay isFadingOut={isFadingOut} />}
    </Box>
  );
};

const DemoVibeCodeButton = () => {
  const { merlin } = useDevSandbox();

  return <VibeCodeButton isActive={merlin.isOpen} onClick={merlin.onToggle} showBubble />;
};


// Mobile auto-spell: shows loading overlay, waits a short period, then injects spell code.
// If user edits code after spell applies, the spell is marked inactive (user takes control).
const MobileAutoSpell = () => {
  const { codeTabs, handlers, merlin: { config: merlinContextConfig }, demoSpell: { state: demoSpellState, setState: setDemoSpellState } } = useDevSandbox();
  const firedRef = useRef(false);

  // Cast spell on mount (only for custom template — render guard is in Demo component)
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    const randomIdx = getRandomSpellIndex(demoSpellState.spellIndex);
    const spell = DEMO_SPELLS[randomIdx];

    // Always restore to original template code (not live editor state)
    const originalCode = merlinContextConfig?.originalCode || { html: '', css: '', js: '' };

    // Mark as loading — preview shows overlay
    setDemoSpellState(() => ({
      isSpellActive: false,
      isSpellLoading: true,
      savedCode: originalCode,
      spellIndex: randomIdx,
    }));

    // Apply spell code after a delay (simulates "casting" time on mobile)
    // Reveal immediately — the 600ms CSS fade-out in DemoPreview covers iframe rebuild time
    const applyId = setTimeout(() => {
      handlers.onHtmlChange(spell.html);
      handlers.onCssChange(spell.css);
      handlers.onJsChange(spell.js);
      setDemoSpellState((prev) => ({ ...prev, isSpellActive: true, isSpellLoading: false }));
    }, 2000);

    return () => {
      clearTimeout(applyId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect user edits: if code changes after spell is active, mark spell inactive
  const spellAppliedRef = useRef(false);
  useEffect(() => {
    if (!demoSpellState.isSpellActive) return;

    // Skip the first code change (that's the spell being applied)
    if (!spellAppliedRef.current) {
      spellAppliedRef.current = true;
      return;
    }

    // User edited code — spell is removed, user has control
    setDemoSpellState((prev) => ({ ...prev, isSpellActive: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeTabs.html, codeTabs.css, codeTabs.js]);

  return null;
};

const CustomUrlSync = () => {
  const { codeTabs, editorSettings, demoSpell } = useDevSandbox();
  const [, setSearchParams] = useSearchParams();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // Don't sync spell code into URL — spells are temporary and should not
    // contaminate the shareable URL. Only sync user-authored code.
    if (demoSpell.state.isSpellActive || demoSpell.state.isSpellLoading) return;

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const params = new URLSearchParams();
      const htmlEncoded = encodeParam(codeTabs.html || '');
      const cssEncoded = encodeParam(codeTabs.css || '');
      const jsEncoded = encodeParam(codeTabs.js || '');
      if (htmlEncoded) params.set('html', htmlEncoded);
      if (cssEncoded) params.set(getCssParamName(editorSettings.cssMode), cssEncoded);
      if (jsEncoded) params.set(getJsParamName(editorSettings.framework), jsEncoded);
      setSearchParams(params, { replace: true });
    }, 1000);

    return () => clearTimeout(timerRef.current);
  }, [codeTabs.html, codeTabs.css, codeTabs.js, editorSettings.framework, editorSettings.cssMode, setSearchParams, demoSpell.state.isSpellActive, demoSpell.state.isSpellLoading]);

  return null;
};

// Applies framework/cssMode settings once on mount based on template or URL params
const DemoSettingsSync = ({ framework, cssMode }: { framework: TEditorFramework; cssMode: TCSSMode }) => {
  const { handlers } = useDevSandbox();
  const appliedRef = useRef(false);

  useEffect(() => {
    if (appliedRef.current) return;
    appliedRef.current = true;
    handlers.onEditorSettingsChange({ framework, cssMode });
  }, [framework, cssMode, handlers]);

  return null;
};

const Demo = () => {
  const { template: templateParam } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSmallScreen } = useMediaQueries();

  const isCustom = templateParam === 'custom';

  // Custom template from URL params — only computed once on mount (code syncs via CustomUrlSync)
  // Security: base64-encoded code renders in a sandboxed iframe with Blob URLs,
  // preventing parent DOM access. User code is isolated and safe to share via URL.
  const customTemplateRef = useRef<DemoTemplate | null>(null);
  const customFrameworkRef = useRef<TEditorFramework>('vanilla');
  const customCssModeRef = useRef<TCSSMode>('css');
  const hasUrlCodeRef = useRef(false);
  if (isCustom && !customTemplateRef.current) {
    const { code: jsCode, framework } = resolveJsParam(searchParams);
    const { code: cssCode, cssMode } = resolveCssParam(searchParams);
    customFrameworkRef.current = framework;
    customCssModeRef.current = cssMode;

    const hasUrlCode = !!(searchParams.get('html') || searchParams.get('js') || searchParams.get('jsx') ||
      searchParams.get('vue') || searchParams.get('ts') || searchParams.get('css') ||
      searchParams.get('scss') || searchParams.get('postcss') || searchParams.get('cssmodules'));
    hasUrlCodeRef.current = hasUrlCode;

    customTemplateRef.current = {
      id: 'custom',
      title: searchParams.get('title') || 'Custom Demo',
      description: 'Custom code loaded from URL',
      icon: '',
      codeTabs: hasUrlCode
        ? {
            html: decodeParam(searchParams.get('html')),
            css: cssCode,
            js: jsCode,
          }
        : {
            html: CUSTOM_DEFAULT.html,
            css: CUSTOM_DEFAULT.css,
            js: CUSTOM_DEFAULT.js,
          },
    };
  }

  // Auto-open Merlin on desktop for fresh custom template (not when code loaded from shared URL)
  const shouldAutoOpenMerlin = !isSmallScreen && isCustom && !hasUrlCodeRef.current;

  const getInitialTemplate = (): DemoTemplate => {
    if (isCustom && customTemplateRef.current) return customTemplateRef.current;
    return VISIBLE_TEMPLATES.find((t) => t.id === templateParam) || VISIBLE_TEMPLATES[0];
  };

  const [selectedTemplate, setSelectedTemplate] = useState<DemoTemplate>(getInitialTemplate);

  const isEmbedRef = useRef(searchParams.get('embed') === 'true');
  const isEmbed = isEmbedRef.current;

  const isRedirect = searchParams.get('redirect') === 'true';
  const isSessionExpired = searchParams.get('session_expired') === 'true';
  const [showRedirectModal, setShowRedirectModal] = useState(isRedirect || isSessionExpired);

  const handleCloseRedirectModal = () => {
    setShowRedirectModal(false);
    searchParams.delete('redirect');
    searchParams.delete('session_expired');
    setSearchParams(searchParams, { replace: true });
  };

  const handleTemplateSelect = (id: string) => {
    navigate(`/demo/${id}`, { replace: true });
  };

  // Detect framework for current template
  const detectedFramework: TEditorFramework = isCustom
    ? customFrameworkRef.current
    : detectFrameworkFromCode(selectedTemplate.codeTabs.js);
  const detectedCssMode: TCSSMode = isCustom ? customCssModeRef.current : 'css';

  useEffect(() => {
    if (!templateParam) return;
    if (isCustom && customTemplateRef.current) {
      setSelectedTemplate(customTemplateRef.current);
    } else {
      const found = VISIBLE_TEMPLATES.find((t) => t.id === templateParam);
      if (found) {
        setSelectedTemplate(found);
      }
    }
  }, [templateParam, isCustom]);

  return (
    <>
      {!isEmbed && <RedirectModal isOpen={showRedirectModal} onClose={handleCloseRedirectModal} isSessionExpired={isSessionExpired} />}
      <SEO
        title={`BigDevSoon | ${selectedTemplate.title} Demo`}
        description={`Try building a ${selectedTemplate.title} in our online code editor.`}
        url={`https://app.bigdevsoon.me/demo/${selectedTemplate.id}`}
        canonical={`https://app.bigdevsoon.me/demo/${selectedTemplate.id}`}
        indexable
      />
      <DevSandbox
        key={selectedTemplate.id}
        id={`demo-${selectedTemplate.id}`}
        title={selectedTemplate.title}
        mode={isEmbed ? 'embed' : 'interactive'}
        merlinConfig={{
          entityName: selectedTemplate.title,
          entityType: 'demo',
          isDemo: true,
          autoOpen: shouldAutoOpenMerlin,
          isCustomTemplate: isCustom,
          // Always use CUSTOM_DEFAULT for originalCode so "Remove Spell" reverts to the
          // clean template, not stale spell code from URL params (CustomUrlSync contamination).
          originalCode: isCustom
            ? { html: CUSTOM_DEFAULT.html, css: CUSTOM_DEFAULT.css, js: CUSTOM_DEFAULT.js }
            : { html: selectedTemplate.codeTabs.html, css: selectedTemplate.codeTabs.css, js: selectedTemplate.codeTabs.js },
        }}
        codeTabs={{
          // When auto-spell is enabled (desktop custom), use CUSTOM_DEFAULT as initial code
          // to prevent stale spell code in URL params from becoming the "template".
          html: shouldAutoOpenMerlin ? CUSTOM_DEFAULT.html : selectedTemplate.codeTabs.html,
          css: shouldAutoOpenMerlin ? CUSTOM_DEFAULT.css : selectedTemplate.codeTabs.css,
          js: shouldAutoOpenMerlin ? CUSTOM_DEFAULT.js : selectedTemplate.codeTabs.js,
        }}
        flags={{ loadCodeFromServer: true }}
        paneTabs={{
          tabs: [
            {
              screenName: 'browser',
              title: 'Browser',
              icon: null,
              content: (
                <>
                  <DemoSettingsSync framework={detectedFramework} cssMode={detectedCssMode} />
                  {isCustom && !isEmbed && <CustomUrlSync />}
                  {isSmallScreen && !isEmbed && isCustom && <MobileAutoSpell />}
                  <DemoPreview />
                </>
              ),
            },
          ],
        }}
        navigation={{
          leftSide: (
            <TemplateSelector
              templates={VISIBLE_TEMPLATES}
              selected={selectedTemplate.id}
              onSelect={handleTemplateSelect}
            />
          ),
          header: <DemoVibeCodeButton />,
          rightSide: isSmallScreen
            ? localStorage.getItem('bds-user-id')
              ? null
              : <GitHubButton text="Sign in" />
            : localStorage.getItem('bds-user-id')
              ? <HStack spacing={2}><EditorSettingsButton /><ShareButton /></HStack>
              : <HStack spacing={2}><EditorSettingsButton /><ShareButton /><GitHubButton text="Sign in with GitHub" /></HStack>,
        }}
        footer={{
          assetsConfig: {
            showDesignTab: false,
            showYourFilesTab: false,
            showPackagesTab: true,
            showToolsTab: false,
          },
        }}
      />
    </>
  );
};

export default Demo;
